/* eslint-disable jsx-a11y/anchor-is-valid */
import { type PropsWithChildren, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRepubContext } from '@cx-web/feature-repub';
import { REPUB_CONFIG } from '@cx-web/repub-config';
import { Divider } from '@fpsm/cxy-components/Divider';
import { CircleCheckIcon, LockIcon, SquareCheckIcon } from '@fpsm/cxy-icons/glyphs';

import { REPUBS } from '@cx-modules/constants';

import {
  useAuthModal,
  type View,
  VIEW_FORGOT_PASSWORD,
  VIEW_LOGIN,
  VIEW_SIGNUP,
  VIEW_VERIFY_EMAIL,
  VIEW_VERIFY_PAYMENT,
} from '../auth-modal-provider';

import { ForgotPasswordForm } from './components/ForgotPasswordForm';
import { LoginForm } from './components/LoginForm';
import { SignupForm } from './components/SignupForm';
import { SocialButtons } from './components/SocialButtons';
import { VerifyEmail } from './components/VerifyEmail';
import { VerifyPayment } from './components/VerifyPayment';
import styles from './styles/Authentication.module.scss';

type TFunction = ReturnType<typeof useTranslation<['label']>>['t'];

export type InfoSectionProps = {
  resourcesUrl: string;
  view: View;
};

export const InfoLink = () => {
  const { repubPath } = useRepubContext();
  return (
    <p className={styles.infoLink}>
      For more information <a href={`${repubPath}/plus`}>click here</a>
    </p>
  );
};

const FEATURE_LIST_ICONS = {
  'circle-check': <CircleCheckIcon />,
  'square-check': <SquareCheckIcon />,
};

const FeatureList = ({
  featureList,
}: {
  featureList: {
    title: string;
  }[];
}) => {
  const { repub } = useRepubContext();
  const iconKey = REPUB_CONFIG[repub]?.plus?.auth.featureListIcon;

  return (
    <ul className={styles.featureList}>
      {featureList.map((feature, index) => (
        <li key={index}>
          {iconKey && FEATURE_LIST_ICONS[iconKey]}
          <span>{feature.title}</span>
        </li>
      ))}
    </ul>
  );
};

export const InfoSection = ({ children, resourcesUrl }: PropsWithChildren<InfoSectionProps>) => {
  const { t } = useTranslation(['label']);
  const { repub } = useRepubContext();

  // Get config for current repub, fallback to OC
  const config = REPUB_CONFIG[repub]?.plus || REPUB_CONFIG[REPUBS.OC.name]?.plus;

  return (
    <div className={styles.infoSection} data-testid="info-section">
      <div>
        <div className={styles.logoContainer}>
          <img alt={config?.auth.logoAlt} src={config?.auth.logoSrc(resourcesUrl)} />
        </div>
        <h2 className={styles.header}>{config?.auth.header(t)}</h2>
        <p>{config?.auth.tagline(t)}</p>
        <FeatureList featureList={config?.auth.featureList ?? []} />
        {children}
      </div>
    </div>
  );
};

InfoSection.InfoLink = InfoLink;

export type AuthSectionProps = {
  /** Base path the auth forms post to, e.g. `/plus/auth` or `/auth`. Defaults to `/plus/auth`. */
  authBasePath?: string;
  currentUrl: string;
  googleID: string;
  /** The info panel is OC+-branded (logo, tagline, feature list) — hide it for non-plus login/register
   * flows. Also threaded down to SignupForm, which uses it to tell plus signups (which read every
   * checkbox's visibility from REPUB_CONFIG[repub]?.plus?.auth, untouched by this) apart from
   * non-plus ones (which have no plus config to read at all for most repubs). */
  hideInfoPanel?: boolean;
  view: View;
};

interface GetAuthSectionHeaderText {
  isForgotPassword: boolean;
  isPlus: boolean;
  isSignup: boolean;
  isVerifyingPayment: boolean;
  t: TFunction;
}

const getAuthSectionHeaderText = ({
  isForgotPassword,
  isPlus,
  isSignup,
  isVerifyingPayment,
  t,
}: GetAuthSectionHeaderText) => {
  const text = isForgotPassword
    ? t('CX_AUTH_FORGOT_PASSWORD')
    : isVerifyingPayment
      ? t('CX_AUTH_ONE_MOMENT')
      : isSignup
        ? t(isPlus ? 'CX_AUTH_SIGN_UP_TO_UNLOCK' : 'CX_AUTH_SIGNUP')
        : t(isPlus ? 'CX_AUTH_LOGIN_TO_UNLOCK' : 'CX_AUTH_LOGIN');

  const shouldDisplayPadlock = isPlus && !isVerifyingPayment;

  return shouldDisplayPadlock ? (
    <>
      <LockIcon />
      {text}
    </>
  ) : (
    text
  );
};

const AuthSection = ({ authBasePath = '/plus/auth', currentUrl, googleID, hideInfoPanel, view }: AuthSectionProps) => {
  const { repub, repubPath } = useRepubContext();
  const { close, openAtView, prefillEmail } = useAuthModal();

  const isForgotPassword = view === VIEW_FORGOT_PASSWORD;
  const isSignup = view === VIEW_SIGNUP;
  const isVerifyingEmail = view === VIEW_VERIFY_EMAIL;
  const isVerifyingPayment = view === VIEW_VERIFY_PAYMENT;

  const { t } = useTranslation(['label']);

  const headingText = getAuthSectionHeaderText({
    isPlus: !hideInfoPanel,
    isForgotPassword,
    isSignup,
    isVerifyingPayment,
    t,
  });

  return (
    <div className={styles.authSection} data-testid="auth-section">
      <div className="flex flex-col space-y-2">
        {!isVerifyingEmail && <h2>{headingText}</h2>}
        {isVerifyingPayment ? (
          <VerifyPayment />
        ) : isVerifyingEmail ? (
          <VerifyEmail
            authBasePath={authBasePath}
            email={prefillEmail}
            hideInfoPanel={hideInfoPanel}
            repubPath={repubPath}
          />
        ) : isForgotPassword ? (
          <ForgotPasswordForm authBasePath={authBasePath} prefillEmail={prefillEmail} repubPath={repubPath} />
        ) : (
          <>
            {REPUB_CONFIG[repub]?.plus?.auth.showSocialLogin && (
              <>
                <SocialButtons
                  authBasePath={authBasePath}
                  currentUrl={currentUrl}
                  googleID={googleID}
                  repubPath={repubPath}
                  view={view}
                />
                <Divider text="OR" />
              </>
            )}
            {isSignup ? (
              <SignupForm
                authBasePath={authBasePath}
                close={close}
                hideInfoPanel={hideInfoPanel}
                openAtView={openAtView}
                prefillEmail={prefillEmail}
                repub={repub}
                repubPath={repubPath}
              />
            ) : (
              <LoginForm
                authBasePath={authBasePath}
                close={close}
                openAtView={openAtView}
                repub={repub}
                repubPath={repubPath}
              />
            )}
          </>
        )}
        {(isForgotPassword || isSignup) && (
          <p className={styles.switchAuthText}>
            {isSignup ? `${t('CX_AUTH_ALREADY_HAVE_ACCOUNT')} ` : `${t('CX_AUTH_DONT_HAVE_ACCOUNT')} `}
            <a
              href={`${repubPath}${authBasePath}/${isSignup ? 'login' : 'signup'}`}
              onClick={e => {
                e.preventDefault();
                openAtView(isForgotPassword ? VIEW_SIGNUP : isSignup ? VIEW_LOGIN : VIEW_SIGNUP);
              }}
            >
              {isSignup ? t('CX_AUTH_LOGIN') : t('CX_AUTH_SIGNUP')}
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export type AuthenticationProps = AuthSectionProps & {
  /** Rendered inside the info panel below the feature list (e.g. a "learn more" link). Ignored when `hideInfoPanel`. */
  infoLink?: ReactNode;
  resourcesUrl: string;
};

function Authentication({ hideInfoPanel, infoLink, resourcesUrl, ...authSectionProps }: AuthenticationProps) {
  return (
    // `@fpsm/cxy-components` (SocialButton, Button, etc.) read their colors from `--cxy-color-*`
    // custom properties that `@fpsm/cxy-tokens` scopes per `[data-fpsm-theme=...]` block. This
    // component was built and only ever visually verified against the `plus` block (product-plus's
    // `<body>` already carries `data-fpsm-theme="plus"`). Non-plus hosts (atlas, atlas-astro) set
    // `data-fpsm-theme="oddschecker"` on their own `<body>` for their own (unrelated) fpsm usage —
    // that theme's token block is missing entries this component depends on (e.g.
    // `--cxy-color-border-input`) and defines others (e.g. the "secondary" button's background)
    // to different, on-brand-for-oddschecker-but-wrong-for-this-modal values. Pinning `plus` here,
    // on the modal's own root, shadows whatever theme the host page set for just this subtree — so
    // the modal always renders identically to product-plus, regardless of where it's mounted.
    <div className={styles.authenticationContainer} data-fpsm-theme="plus" data-testid="auth-modal">
      {!hideInfoPanel && (
        <InfoSection resourcesUrl={resourcesUrl} view={authSectionProps.view}>
          {infoLink}
        </InfoSection>
      )}
      <AuthSection hideInfoPanel={hideInfoPanel} {...authSectionProps} />
    </div>
  );
}

Authentication.AuthSection = AuthSection;
Authentication.InfoSection = InfoSection;

export { Authentication };
